import { ANNOTATION_SOURCE_LOCATION } from '@backstage/catalog-model';
import {
  CodeSnippet,
  EmptyState,
  Progress,
  WarningPanel,
} from '@backstage/core-components';
import { useApi } from '@backstage/core-plugin-api';
import { scmIntegrationsApiRef } from '@backstage/integration-react';
import { useAsyncEntity } from '@backstage/plugin-catalog-react';
import { ReactElement, useState } from 'react';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import DialogContentText from '@mui/material/DialogContentText';
import Typography from '@mui/material/Typography';

const NEBULA_MIGRATION_URL =
  'https://odp.i.mercedes-benz.com/docs/default/component/esg-nebula-platform-scm/getting-started/';

const NON_NEBULA_MIGRATION_URL =
  'https://pages.git.i.mercedes-benz.com/GitHub/Home/docs/GitHubCloud/Server2cloud/prerequisites';

const MigrationGuideDialog = ({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) => {
  const handleSelect = (isNebula: boolean) => {
    window.open(
      isNebula ? NEBULA_MIGRATION_URL : NON_NEBULA_MIGRATION_URL,
      '_blank',
      'noopener,noreferrer',
    );
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        GitHub Enterprise Server → GitHub Enterprise Cloud Migration
      </DialogTitle>
      <DialogContent>
        <DialogContentText component="div">
          <Typography variant="body1" gutterBottom>
            This guide will help you migrate your component's source repository
            from <strong>GitHub Enterprise Server</strong> to{' '}
            <strong>GitHub Enterprise Cloud</strong>.
          </Typography>
          <Typography variant="body1" sx={{ mt: 3 }}>
            To direct you to the correct migration guide, please tell us:
          </Typography>
          <Typography variant="body1" sx={{ ml: 2 }}>
            Is the tenant that owns this component a <strong>Nebula</strong>{' '}
            tenant?
          </Typography>
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="inherit">
          Cancel
        </Button>
        <Button
          variant="outlined"
          color="primary"
          onClick={() => handleSelect(false)}
        >
          No, not a Nebula tenant
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={() => handleSelect(true)}
        >
          Yes, Nebula tenant
        </Button>
      </DialogActions>
    </Dialog>
  );
};

type GitHubGuardProps = {
  feature?: string;
  children: ReactElement;
};

export const GitHubGuard = ({ children, feature }: GitHubGuardProps) => {
  const { entity, loading, error } = useAsyncEntity();
  const scmIntegrations = useApi(scmIntegrationsApiRef);
  const [migrationDialogOpen, setMigrationDialogOpen] = useState(false);

  const githubIntegrations = scmIntegrations.github.list();

  if (githubIntegrations.length === 0) {
    return (
      <div>
        <WarningPanel
          severity="warning"
          title="No GitHub integration available. Please configure at least one GitHub instance."
        />
      </div>
    );
  }

  if (loading) {
    return <Progress />;
  }

  if (error) {
    return (
      <div>
        <WarningPanel severity="error" title="Could not load entity.">
          <CodeSnippet language="text" text={error.toString()} />
        </WarningPanel>
      </div>
    );
  }

  const gitHubPrimaryHost = scmIntegrations.github.list()[0].config.host;
  const sourceLocation =
    entity?.metadata.annotations?.[ANNOTATION_SOURCE_LOCATION];
  const allowed = sourceLocation && sourceLocation.includes(gitHubPrimaryHost);

  return (
    <>
      <MigrationGuideDialog
        open={migrationDialogOpen}
        onClose={() => setMigrationDialogOpen(false)}
      />
      {allowed ? (
        children
      ) : (
        <EmptyState
          title={
            feature ? `Access Denied to GitHub ${feature}` : `Access Denied`
          }
          missing="data"
          description={`The entity's source location is not from the primary GitHub instance (${gitHubPrimaryHost}). Please migrate your components from the old GitHub to enable this functionality.`}
          action={
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              <Button
                variant="contained"
                color="primary"
                onClick={() => setMigrationDialogOpen(true)}
              >
                Follow Migration Guide
              </Button>
              <Button
                variant="outlined"
                color="primary"
                href="/academy/default/tutorial/odp-odp-academy-odp-github-migration"
              >
                Activate Scanning
              </Button>
            </div>
          }
        />
      )}
    </>
  );
};
